import * as THREE from 'three';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { PlaneVolume } from './Volume.js';

export class PlaneTool extends EventDispatcher {
    constructor(viewer) {
        super();
        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.scene = new THREE.Scene();
        this.scene.name = 'scene_plane';

        this.viewer.inputHandler.registerInteractiveScene(this.scene);

        this.addEventListener('start_inserting_plane', (e) => {
            this.viewer.dispatchEvent({
                type: 'cancel_insertions',
            });
        });

        this.onRemove = (e) => {
            this.scene.remove(e.plane);
        };

        this.onAdd = (e) => {
            this.scene.add(e.plane);
        };

        for (let plane of viewer.scene.planes || []) {
            this.onAdd({ plane: plane });
        }

        this.viewer.inputHandler.addEventListener('delete', (event) => {
            const { selection } = event;

            selection.forEach((item) => {
                if (item instanceof PlaneVolume) {
                    // delete plane asset
                    if (confirm('Delete plane asset?')) {
                        this.viewer.scene.removePlane(item);
                    }
                }
            });
        });

        window.addEventListener('keydown', (event) => {
            const deleteKeyCodes = new Set(['Delete', 'Backspace']);
            const isEditing = ['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.isContentEditable;

            if (isEditing || !deleteKeyCodes.has(event.code) || !(this.viewer && this.viewer.inputHandler)) return;

            const { inputHandler } = this.viewer;
            const { selection } = inputHandler;

            const selectedPlanes = selection.filter((item) => item instanceof PlaneVolume);

            if (selectedPlanes.length === 0) return;

            inputHandler.dispatchEvent({
                type: 'delete',
                selection: selectedPlanes,
            });
        });

        viewer.addEventListener('update', this.update.bind(this));
        viewer.addEventListener('render.pass.scene', (e) => this.render(e));
        viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

        viewer.scene.addEventListener('plane_added', this.onAdd);
        viewer.scene.addEventListener('plane_removed', this.onRemove);
    }

    onSceneChange(e) {
        if (e.oldScene) {
            e.oldScene.removeEventListeners('plane_added', this.onAdd);
            e.oldScene.removeEventListeners('plane_removed', this.onRemove);
        }

        e.scene.addEventListener('plane_added', this.onAdd);
        e.scene.addEventListener('plane_removed', this.onRemove);
    }

    addPlane(plane) {
        this.viewer.scene.planes = this.viewer.scene.planes || [];
        this.viewer.scene.planes.push(plane);
        this.scene.add(plane);

        this.viewer.scene.dispatchEvent({
            type: 'plane_added',
            plane: plane,
        });
    }

    startInsertion(args = {}, customCallback) {
        this.ifcHelper = args.ifcHelper || null;

        const plane = new PlaneVolume({ color: args.color });
        plane.name = args.name || 'Plane';
        plane.locked = false;
        plane.newlyCreated = true;

        this.dispatchEvent({
            type: 'start_inserting_plane',
            plane: plane,
        });

        this.addPlane(plane);

        let cancel = {
            callback: null,
        };

        let drag = async (e) => {
            const camera = this.viewer.scene.getActiveCamera();
            const mousePosition = e.drag.end;

            let I = Utils.getMousePointCloudIntersection(mousePosition, camera, this.viewer, this.viewer.scene.pointclouds, {
                pickClipped: true,
                pickInvisible: false,
            });

            if (!I && this.ifcHelper) {
                const ifcResult = await this.ifcHelper.castRayIfc();
                if (ifcResult) {
                    I = {
                        location: ifcResult.point,
                        object: ifcResult.object,
                        faceIndex: ifcResult.faceIndex,
                    };
                }
            }

            if (I) {
                plane.position.copy(I.location);

                let distance = I.location.distanceTo(camera.position);
                let scaleFactor = distance / 10;
                plane.scale.set(scaleFactor, scaleFactor, scaleFactor);
            }
        };

        let drop = (e) => {
            plane.removeEventListener('drag', drag);
            plane.removeEventListener('drop', drop);

            cancel.callback(e);
        };

        cancel.callback = (e) => {
            plane.removeEventListener('drag', drag);
            plane.removeEventListener('drop', drop);
            this.viewer.removeEventListener('cancel_insertions', cancel.callback);
            if (customCallback) {
                customCallback(e.target);
            }
        };

        plane.addEventListener('drag', drag);
        plane.addEventListener('drop', drop);
        this.viewer.addEventListener('cancel_insertions', cancel.callback);

        this.viewer.inputHandler.startDragging(plane);

        return plane;
    }

    update() {
        if (!this.viewer.scene) {
            return;
        }

        const planes = this.viewer.scene.planes || [];
        for (let plane of planes) {
            if (plane.label) {
                let camera = this.viewer.scene.getActiveCamera();
                let distance = plane.label.position.distanceTo(camera.position);
                let renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
                let clientWidth = renderAreaSize.width;
                let clientHeight = renderAreaSize.height;

                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
                let scale = 70 / pr;
                plane.label.scale.set(scale, scale, scale);
            }
        }
    }

    render(params) {
        const renderer = this.renderer;
        const oldTarget = renderer.getRenderTarget();

        if (params.renderTarget) {
            renderer.setRenderTarget(params.renderTarget);
        }

        renderer.render(this.scene, this.viewer.scene.getActiveCamera());
        renderer.setRenderTarget(oldTarget);
    }
}
